import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Search, Calendar, Clock, MapPin, CheckCircle, XCircle, Edit, Plus, Minus , RotateCcw} from 'lucide-react';
import patientService from '../../patient/services/patientService';

const DoctorPatientBookingsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [patient, setPatient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [previousBookings, setPreviousBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // State for search inputs (editable)
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for collapsible search section (closed by default)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Set page title and fetch data
  useEffect(() => {
    document.title = 'MediFusion - Patient Bookings';
    fetchPatientData();
    fetchBookingData();
  }, [patientId]);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch patient data
  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('📋 Patient data received:', patientData);
      
      setPatient(patientData);
    } catch (error) {
      console.error('❌ Error fetching patient data:', error);
      showToast('Failed to load patient data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch booking data from the database
  const fetchBookingData = async () => {
    try {
      console.log('🔍 Fetching bookings for patient:', patientId);
      
      // Fetch real booking data for the patient
      const patientBookings = await patientService.getPatientBookingsByPatientId(patientId);
      console.log('📋 Patient bookings received:', patientBookings);
      
      if (!patientBookings || patientBookings.length === 0) {
        console.log('ℹ️ No bookings found for patient:', patientId);
        setBookings([]);
        setUpcomingBookings([]);
        setPreviousBookings([]);
        return;
      }

      // Get today's date for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Transform and categorize bookings
      const transformedBookings = patientBookings.map(booking => {
        // Format date from YYYY-MM-DD to DD/MM/YYYY
        const date = new Date(booking.appointment_date);
        const formattedDate = date.toLocaleDateString('en-AU'); // DD/MM/YYYY format
        
        // Format time from HH:MM:SS to HH:MM AM/PM
        let formattedTime = 'Time TBD';
        if (booking.appointment_time) {
          const time = new Date(`2000-01-01T${booking.appointment_time}`);
          formattedTime = time.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
          }).toLowerCase();
        }

        console.log('🔍 Booking details:', {
          id: booking.id,
          appointment_date: booking.appointment_date,
          status: booking.status,
          appointment_type: booking.appointment_type,
          doctor_name: booking.doctor_name,
          doctor: booking.doctor,
          clinic_location: booking.clinic_location,
          clinic_name: booking.clinic_name,
          clinic_address: booking.clinic_address
        });

        return {
          id: booking.id,
          date: formattedDate,
          time: formattedTime,
          appointmentType: booking.appointment_type || 'Consultation',
          doctor: booking.doctor_name || booking.doctor || 'Dr. TBD',
          facility: booking.clinic_name || booking.clinic_location || 'MediFusion Clinic',
          address: booking.clinic_address || 'Address TBD',
          status: booking.status,
          bookingId: booking.booking_id,
          originalDate: booking.appointment_date,
          originalTime: booking.appointment_time
        };
      });

      // Separate upcoming and previous bookings
      const allUpcomingCandidates = [];
      const previous = [];

      transformedBookings.forEach(booking => {
        const bookingDate = new Date(booking.originalDate);
        bookingDate.setHours(0, 0, 0, 0);
        
        console.log('📅 Booking date analysis:', {
          bookingId: booking.id,
          originalDate: booking.originalDate,
          bookingDate: bookingDate,
          today: today,
          isDateFuture: bookingDate >= today,
          status: booking.status,
          isStatusValid: ['Scheduled', 'scheduled', 'SCHEDULED'].includes(booking.status)
        });
        
        // Check if booking is upcoming (future date and scheduled status)
        // Accept multiple case variations of "scheduled"
        const isScheduledStatus = ['Scheduled', 'scheduled', 'SCHEDULED', 'pending', 'Pending', 'PENDING'].includes(booking.status);
        
        if (bookingDate >= today && isScheduledStatus) {
          console.log('✅ Adding to upcoming candidates:', booking.id);
          allUpcomingCandidates.push(booking);
        } else {
          console.log('📜 Adding to previous bookings:', booking.id, 'Reason:', 
            bookingDate < today ? 'Past date' : 'Status not scheduled');
          previous.push(booking);
        }
      });

      // Sort upcoming candidates by date and time (earliest first)
      allUpcomingCandidates.sort((a, b) => {
        const dateA = new Date(a.originalDate);
        const dateB = new Date(b.originalDate);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        // If same date, sort by time
        return a.originalTime.localeCompare(b.originalTime);
      });
      
      // Only show the earliest upcoming booking in "upcoming", rest go to "previous"
      const upcoming = [];
      if (allUpcomingCandidates.length > 0) {
        upcoming.push(allUpcomingCandidates[0]); // Only the earliest
        // Add the rest to previous bookings
        allUpcomingCandidates.slice(1).forEach(booking => {
          console.log('📜 Moving non-earliest upcoming booking to previous:', booking.id);
          previous.push(booking);
        });
      }
      
      // Sort previous bookings by date (most recent first)
      previous.sort((a, b) => {
        const dateA = new Date(a.originalDate);
        const dateB = new Date(b.originalDate);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB - dateA; // Most recent first
        }
        // If same date, sort by time (latest first)
        return b.originalTime.localeCompare(a.originalTime);
      });

      console.log('📅 Upcoming bookings:', upcoming);
      console.log('📜 Previous bookings:', previous);
      console.log('📊 Total transformed bookings:', transformedBookings.length);
      console.log('📊 Upcoming count:', upcoming.length);
      console.log('📊 Previous count:', previous.length);
      console.log('📊 Total displayed:', upcoming.length + previous.length);

      setBookings(transformedBookings);
      setUpcomingBookings(upcoming);
      setPreviousBookings(previous);

    } catch (error) {
      console.error('❌ Error fetching booking data:', error);
      showToast('Failed to load booking data', 'error');
      // Set empty arrays on error
      setBookings([]);
      setUpcomingBookings([]);
      setPreviousBookings([]);
    }
  };

  // Initialize patient data when component loads
  useEffect(() => {
    if (patient) {
      setSearchData({
        patientId: patient.patient_id || '',
        patientName: `${patient.first_name || ''} ${patient.last_name || ''}`.trim(),
        patientDOB: patient.dob || ''
      });
      setIsPatientSelected(true);
    }
  }, [patient]);

  // Fetch all patients for search functionality
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      const response = await patientService.getAllPatients();
      console.log('👥 Patients from API:', response);
      
      // The response should have a patients array and metadata
      if (response && response.patients && Array.isArray(response.patients)) {
        setAllPatients(response.patients);
        console.log(`✅ Successfully loaded ${response.patients.length} patients for search`);
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        setAllPatients([]);
      }
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      setAllPatients([]);
      showToast('Failed to load patients for search', 'error');
    } finally {
      setLoadingPatients(false);
    }
  };

  // Load patients on component mount
  useEffect(() => {
    fetchAllPatients();
  }, []);

  // Handle Patient ID input change and filtering
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({ ...prev, patientId: value }));
    
    if (value.trim() === '') {
      setFilteredPatientsById([]);
      setShowPatientIdDropdown(false);
      return;
    }
    
    // Filter patients by ID
    const filtered = allPatients.filter(patient => 
      patient.patient_id && patient.patient_id.toLowerCase().includes(value.toLowerCase())
    );
    
    setFilteredPatientsById(filtered);
    setShowPatientIdDropdown(filtered.length > 0);
  };

  // Handle Patient Name input change and filtering
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({ ...prev, patientName: value }));
    
    if (value.trim() === '') {
      setFilteredPatientsByName([]);
      setShowPatientNameDropdown(false);
      return;
    }
    
    // Filter patients by name
    const filtered = allPatients.filter(patient => {
      const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.toLowerCase();
      return fullName.includes(value.toLowerCase());
    });
    
    setFilteredPatientsByName(filtered);
    setShowPatientNameDropdown(filtered.length > 0);
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (selectedPatient) => {
    console.log('👤 Patient selected:', selectedPatient);
    setSelectedPatient(selectedPatient);
    setSearchData({
      patientId: selectedPatient.patient_id || '',
      patientName: `${selectedPatient.first_name || ''} ${selectedPatient.last_name || ''}`.trim(),
      patientDOB: selectedPatient.dob || ''
    });
    
    // Hide dropdowns
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setIsPatientSelected(true);
    
    // Navigate to the selected patient's booking page
    navigate(`/doctor/patientfiles/${selectedPatient.patient_id}/bookings`);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    console.log('🔍 Search submitted with data:', searchData);
    
    if (!searchData.patientId.trim()) {
      showToast('Please enter a Patient ID to search', 'error');
      return;
    }
    
    // Navigate to the searched patient's details
    navigate(`/doctor/patientfiles/${searchData.patientId.trim()}/bookings`);
  };

  // Handle new patient button
  const handleNewPatient = () => {
    console.log('🆕 New Patient button clicked - clearing search form');
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Hide dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowPatientIdDropdown(false);
      setShowPatientNameDropdown(false);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Helper function to format status display
  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    
    if (status === 'Scheduled' || status === 'scheduled') return 'Scheduled';
    if (status === 'Completed' || status === 'completed') return 'Completed';
    if (status === 'cancelled' || status === 'Cancelled') return 'Cancelled';
    
    // For any other status, format as title case
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Helper function to get status badge color
  const getStatusColor = (status) => {
    if (status === 'Scheduled' || status === 'scheduled') return 'bg-yellow-100 text-yellow-800';
    if (status === 'Completed' || status === 'completed') return 'bg-green-100 text-green-800';
    if (status === 'cancelled' || status === 'Cancelled') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Handle booking action (Edit, Cancel, Go To Appt)
  const handleBookingAction = (action, bookingId, booking) => {
    console.log(`${action} booking:`, bookingId);
    
    if (action === 'Go To Appt') {
      // Navigate based on appointment type
      console.log('🔍 Patient booking clicked:', booking);
      console.log('🔍 appointment_type:', booking?.appointment_type);
      console.log('🔍 appointmentType:', booking?.appointmentType);
      
      // Pass booking data through navigation state
      const appointmentData = {
        id: booking.id,
        patient_id: booking.patient_string_id || booking.patient_id,  // Use string ID for patient lookup
        patient_name: booking.patient_name || `${booking.patient_first_name || ''} ${booking.patient_last_name || ''}`.trim(),
        doctor_name: booking.doctor_name || booking.doctor,
        appointment_type: booking.appointment_type || booking.appointmentType,
        appointment_date: booking.appointment_date,
        appointment_time: booking.appointment_time,
        reason: booking.reason,
        specialty: booking.specialty,
        clinic_location: booking.clinic_location,
        schedule_type: booking.schedule_type,
        status: booking.status
      };
      
      if (booking?.appointment_type === 'Clinical Telehealth' || booking?.appointment_type === 'clinical-telehealth' || booking?.appointmentType === 'Clinical Telehealth') {
        console.log('🏥 Navigating to Clinical Telehealth with data:', appointmentData);
        navigate(`/doctor/clinicaltelenotes/${booking.id}`, { state: { appointmentData } });
      } else {
        console.log('🏥 Navigating to General Telehealth with data:', appointmentData);
        navigate(`/doctor/generaltelenotes/${booking.id}`, { state: { appointmentData } });
      }
    } else {
      showToast(`${action} action triggered for booking ${bookingId}`, 'success');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading patient data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 text-lg font-medium mb-2">Patient Not Found</div>
            <div className="text-gray-600 mb-4">The requested patient could not be found.</div>
            <button
              onClick={() => navigate('/doctor/patient-files')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Patient Files
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Patient Files</h1>
      </div>

      {/* White Content Container with Search Block */}
      <div className={`bg-white rounded-lg shadow-md mb-4 ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-2">
                {/* Input Fields and Buttons Row - All in One Line */}
                <div className="flex items-end space-x-3">
                  {/* Patient ID Input */}
                  <div className="flex-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                    <input
                      type="text"
                      placeholder="Enter Patient ID"
                      value={searchData.patientId}
                      onChange={(e) => handlePatientIdChange(e.target.value)}
                      onFocus={() => setShowPatientIdDropdown(true)}
                      disabled={isPatientSelected}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    />
                    
                    {/* Dropdown for patient selection by ID */}
                    {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {filteredPatientsById.map((patient) => (
                          <div
                            key={patient.id || patient.patient_id}
                            onClick={() => handlePatientSelect(patient)}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{patient.patient_id}</div>
                            <div className="text-sm text-gray-600">{patient.first_name} {patient.last_name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Patient Name Input */}
                  <div className="flex-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                    <input
                      type="text"
                      placeholder="Enter Patient Name"
                      value={searchData.patientName}
                      onChange={(e) => handlePatientNameChange(e.target.value)}
                      onFocus={() => setShowPatientNameDropdown(true)}
                      disabled={isPatientSelected}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    />
                    
                    {/* Dropdown for patient selection by name */}
                    {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {filteredPatientsByName.map((patient) => (
                          <div
                            key={patient.id || patient.patient_id}
                            onClick={() => handlePatientSelect(patient)}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</div>
                            <div className="text-sm text-gray-500">ID: {patient.patient_id}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Patient DOB Input */}
                  <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient DOB:</label>
                    <input
                      type="date"
                      value={searchData.patientDOB}
                      onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                    />
                  </div>
                  
                  {/* Search Button */}
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                  >
                    <Search className="h-3 w-3" />
                    <span>Search</span>
                  </button>
                  
                  {/* Reset Fields Button */}
                  <button
                    type="button"
                    onClick={handleNewPatient}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                  >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Fields</span>
              </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Patient Name Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg mb-6 shadow-sm relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200 rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-300 rounded-full translate-y-8 translate-x-8"></div>
        </div>
        
        {/* Patient name with enhanced styling */}
        <div className="relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-5 bg-blue-500 rounded-full"></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide leading-tight">
                {patient.first_name} {patient.last_name}
              </h2>
              <div className="h-0.5 w-12 bg-blue-500 mt-1 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Navigation Sidebar and Main Content */}
      <div className="flex gap-6">
        <div className="w-64">
          <div className="bg-white rounded-lg shadow-md p-4">
            <nav className="space-y-2">
              <button className="w-full text-left px-3 py-2 rounded-md bg-blue-600 text-white transition-colors">
                Bookings
              </button>
              <Link 
                to={`/doctor/patientfiles/${patientId}/general_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                General Details
              </Link>
              <Link 
                to={`/doctor/patientfiles/${patientId}/medical_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Medical Details
              </Link>
              <Link 
                to={`/doctor/patientfiles/${patientId}/history`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                History
              </Link>
              <Link 
                to={`/doctor/patientfiles/${patientId}/analytics`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Analytics
              </Link>
              <Link 
                to={`/doctor/patientfiles/${patientId}/documents`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Documents
              </Link>
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 space-y-6">
          {/* Upcoming Bookings Section */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <h4 className="font-semibold">Upcoming Booking</h4>
              </div>
            </div>
            <div className="p-4">
              {upcomingBookings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Date & Time</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Appt. Type</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Doctor</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Facility</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Address</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.date}</div>
                            <div className="text-sm text-gray-600">{booking.time}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.appointmentType}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.doctor}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.facility}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.address}</div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => handleBookingAction('Edit', booking.id)}
                                className="bg-white text-blue-600 border border-blue-600 px-2 py-1 rounded text-xs hover:bg-blue-50 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleBookingAction('Cancel', booking.id)}
                                className="bg-white text-red-600 border border-red-600 px-2 py-1 rounded text-xs hover:bg-red-50 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleBookingAction('Go To Appt', booking.id, booking)}
                                className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 transition-colors"
                              >
                                Go To Appt
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-gray-500">No upcoming bookings found</div>
                </div>
              )}
            </div>
          </div>

          {/* Previous Bookings Section */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <h4 className="font-semibold">Previous Bookings</h4>
              </div>
            </div>
            <div className="p-4">
              {previousBookings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Date & Time</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Appt. Type</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Doctor</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Facility</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 border-r border-gray-200">Address</th>
                        <th className="text-center py-2 px-2 text-sm font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previousBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.date}</div>
                            <div className="text-sm text-gray-600">{booking.time}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.appointmentType}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.doctor}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.facility}</div>
                          </td>
                          <td className="py-3 px-2 border-r border-gray-200 text-center">
                            <div className="text-sm text-gray-900">{booking.address}</div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                              {formatStatus(booking.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-gray-500">No previous bookings found</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300 transform ${
          toast.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          {toast.type === 'error' ? (
            <XCircle className="h-5 w-5" />
          ) : (
            <CheckCircle className="h-5 w-5" />
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default DoctorPatientBookingsPage;
